import React from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { formatPrice } from 'helpers'

import { CheckedList, ProductCountImage, Text } from 'components/dataDisplay'
import { Button } from 'components/inputs'
import ProductPrice from 'compositions/ProductPrice/ProductPrice'

import messages from './messages'
import s from './PlanCard.scss'

import mostPopMobileImage from './images/most-pop-mobile.svg'
import mostPopDesktopImage from './images/most-pop-desktop.svg'


type PlanCardProps = {
  className?: string
  data: SubscriptionModule.UpgradePlan
  isSubscriptionFlow?: boolean
  onClick?: (plan: SubscriptionModule.UpgradePlan) => void
}

const PlanCard: React.FC<PlanCardProps> = ({ className, data, isSubscriptionFlow, onClick }) => {
  const { isMobile } = useDevice()

  const { productsPerPeriod, productPrice, price: planPrice, percentageDiscount: percent, save, discount, selected } = data
  const discountPlanPriceUSD = discount?.defaultDiscountPrice ? discount?.defaultDiscountPrice / 100 : null
  const isCouponDiscountExists = discountPlanPriceUSD && discountPlanPriceUSD < planPrice / 100

  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick(data)
    }
  }

  const isOnePerMonth = productsPerPeriod === 1
  const isMostPopular = productsPerPeriod === 2

  const price = formatPrice(isSubscriptionFlow ? planPrice : productPrice)
  const savePrice = formatPrice(save)

  const checkedListItems: Intl.Message[] = isOnePerMonth
    ? [ messages.oneBottle ]
    : isSubscriptionFlow
      ? [ { ...messages.saveLong, values: { percent, save: savePrice } } ]
      : [ { ...messages.chooseAmount, values: { productsPerPeriod } } ]

  if (isSubscriptionFlow && isCouponDiscountExists) {
    checkedListItems.push({ ...messages.renews, values: { price } })
  }

  const rootClassName = cx(className, 'relative', isMobile || !isMostPopular ? 'pt-80' : isMostPopular && 'pb-40')
  const cardClassName = cx(
    s.card,
    'relative pt-40 pb-24 px-24 h-full flex flex-col items-start justify-start bg-white text-left',
    isMostPopular && `${s.highlight} shadow`
  )

  const isPercentageDiscountVisible = Boolean(!isSubscriptionFlow && percent)

  const circlePercentOffClassName = isPercentageDiscountVisible
    ? cx('absolute top-0 right-0 m-12 flex flex-col justify-center bg-light-beige radius-full text-gold-50 text-center', s.circle)
    : null

  const buttonTitle = !isSubscriptionFlow && save > 0
    ? { ...messages.save, values: { value: savePrice } }
    : { ...messages.choose, values: { count: productsPerPeriod } }

  const title = { ...isSubscriptionFlow ? messages.subscriptionTitle : messages.title, values: { productsPerPeriod } }

  return (
    <div
      className={rootClassName}
      data-testid={`subscriptionPlanProductsPerPeriod_${productsPerPeriod}`}
    >
      <div className={cardClassName}>
        <ProductCountImage
          imageClassName={s.countImage}
          count={productsPerPeriod}
          imageType="flask"
        />
        <Text
          className="mt-24"
          message={title}
          style="sh5"
        />
        {
          isSubscriptionFlow ? (
            <>
              <ProductPrice
                className="mt-8 inline-flex flex-row-reverse items-baseline"
                data={{
                  standardPrice: { amount: planPrice / 100, currencyCode: 'USD' },
                  discountPrice: { amount: discountPlanPriceUSD, currencyCode: 'USD' },
                }}
                textStyle="h3"
                oldPriceClassName={s.oldPrice}
              />
              {
                isCouponDiscountExists && (
                  <Text
                    className="mt-8"
                    message={messages.forFirstMonth}
                    style="sh6"
                    color="gold-50"
                  />
                )
              }
            </>
          ) : (
            <Text
              className="mt-8"
              message={{ ...messages.productPrice, values: { price } }}
              style="sh5"
              html
            />
          )
        }
        <CheckedList className="my-24" items={checkedListItems} />
        {
          selected ? (
            <Text
              className="mt-auto mb-24 w-full"
              message={messages.yourPlan}
              style="eye2"
              color="gray-70"
              align="center"
            />
          ) : (
            <Button
              className="mt-auto"
              title={buttonTitle}
              size={56}
              fullWidth
              rightIcon={isMobile ? null : '32/arrow-right'} // it doesn't fit on mobile
              inlineIcon={isMobile ? '32/arrow-right' : null}
              onClick={handleClick}
              data-testid="subscriptionPlanAction"
            />
          )
        }
        {
          isMostPopular && (
            <img
              className={cx('absolute', s.mostPopImage)}
              src={isMobile ? mostPopMobileImage : mostPopDesktopImage}
              alt="Most popular"
            />
          )
        }
        {
          isPercentageDiscountVisible && (
            <div className={circlePercentOffClassName} data-testid="subscriptionPlanDiscount">
              <div className="text-h5">{percent}</div>
              <Text message={messages.off} style="eye1" />
            </div>
          )
        }
      </div>
    </div>
  )
}


export default React.memo(PlanCard)
